import { useEffect, useRef, useState } from "react";
import { UploadCloud, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";

export const InventoryUpload = () => {
  const ref = useRef();
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);

  const load = () => api.get("/inventory/uploads").then((r) => setLogs(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/inventory/upload", fd);
      setLast(data);
      toast.success(`Imported: ${data.created} new, ${data.updated} updated`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      ref.current.value = "";
    }
  };

  return (
    <div className="space-y-6">
      <div
        className="rounded-lg border-2 border-dashed border-slate-300 bg-white p-10 text-center transition-colors hover:border-[#1BA3E0]"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files[0]); }}
        data-testid="inventory-dropzone"
      >
        <UploadCloud className="mx-auto h-10 w-10 text-[#1BA3E0]" />
        <h3 className="mt-3 font-semibold text-[#0B1523]">Upload weekly inventory & stock count</h3>
        <p className="mx-auto mt-1 max-w-lg text-sm text-slate-500">
          CSV or Excel (.xlsx). Columns: <span className="num">SKU, Name, Price, Cost, Stock, GST</span> (optional: Description, Category, Unit).
          Existing SKUs are updated, new SKUs are added. GST column: Yes/No.
        </p>
        <input ref={ref} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="inventory-file-input" />
        <div className="mt-5 flex justify-center gap-2">
          <Button disabled={busy} onClick={() => ref.current.click()} className="bg-[#01195D] hover:bg-[#0A2A7A]" data-testid="inventory-upload-btn">{busy ? "Uploading…" : "Choose file"}</Button>
          <Button variant="outline" onClick={() => downloadFile("/inventory/template", "inventory_template.csv")} data-testid="inventory-template-btn"><FileDown className="mr-1 h-4 w-4" /> Template</Button>
        </div>
      </div>

      {last && last.errors?.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="inventory-upload-errors">
          <div className="font-semibold">{last.errors.length} row(s) skipped</div>
          <ul className="mt-1 list-disc pl-5">{last.errors.slice(0, 10).map((e) => <li key={e}>{e}</li>)}</ul>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <div className="border-b border-slate-200 p-4 font-semibold text-[#0B1523]">Upload history</div>
        <table className="w-full text-sm" data-testid="inventory-history-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-3">Date</th><th className="px-4 py-3">File</th><th className="px-4 py-3">By</th><th className="px-4 py-3 text-right">Rows</th><th className="px-4 py-3 text-right">New</th><th className="px-4 py-3 text-right">Updated</th><th className="px-4 py-3 text-right">Skipped</th></tr>
          </thead>
          <tbody>
            {logs.map((l) => (
              <tr key={l.id} className="border-t border-slate-100">
                <td className="px-4 py-3 text-slate-500">{new Date(l.uploaded_at).toLocaleString("en-AU")}</td>
                <td className="px-4 py-3 font-medium">{l.filename}</td>
                <td className="px-4 py-3">{l.uploaded_by}</td>
                <td className="num px-4 py-3 text-right">{l.rows}</td>
                <td className="num px-4 py-3 text-right text-emerald-700">{l.created}</td>
                <td className="num px-4 py-3 text-right text-blue-700">{l.updated}</td>
                <td className="num px-4 py-3 text-right text-amber-700">{l.skipped}</td>
              </tr>
            ))}
            {!logs.length && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">No uploads yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};
